import * as React from "react";
import { useAction, useMutation, useQuery } from "convex/react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader } from "@/components/ui/card";
import { useAuthIdentity } from "@/contexts/UserProfileContext";
import { api } from "@/lib/convexApi";
import { formatCurrency, formatDate } from "@/lib/format";
import { hasPermission } from "@/lib/roles";
import { CreditKeyConfirmDialog } from "./CreditKeyConfirmDialog";
import { CreditKeyPanelBoundary } from "./CreditKeyPanelBoundary";
import { CreditKeyOrderReview } from "./CreditKeyOrderReview";
import { CreditKeyStoragePanel } from "./CreditKeyStoragePanel";

const STATUS_LABELS = {
  creating: "Creating checkout",
  ready: "Awaiting customer completion",
  authorizing: "Confirming authorization",
  authorized: "Authorized — not captured",
  capturing: "Confirming capture",
  captured: "Captured — payout not verified here",
  canceling: "Confirming cancellation",
  canceled: "Financing canceled",
  refunding: "Confirming refund",
  refunded: "Refund confirmed",
  review_required: "Needs payment review",
  failed: "Checkout failed",
} as const;

type PaymentStatus = keyof typeof STATUS_LABELS;
type Attempt = {
  _id: string;
  orderId: string;
  status: PaymentStatus;
  amount: number;
  createdAt: number;
  updatedAt: number;
  environment: "sandbox" | "production";
  providerStatus?: string;
  reviewReason?: string;
  canReconcile: boolean;
  canCancel: boolean;
  canCapture: boolean;
  canRefund: boolean;
  canReviewReversal?: boolean;
  inventoryReviewRequired?: boolean;
  canSimulateShipment: boolean;
  disabledReason?: string;
};
type Operation = "reconcile" | "cancel" | "capture" | "refund" | "reviewReversal" | "simulateShipment";
type SelectedOperation = { operation: Operation; attempt: Attempt };

function operationAllowed(attempt: Attempt, operation: Operation): boolean {
  if (!Object.prototype.hasOwnProperty.call(STATUS_LABELS, attempt.status)) return false;
  if (operation === "reconcile") return attempt.canReconcile;
  if (operation === "cancel") return attempt.canCancel;
  if (operation === "refund") return attempt.canRefund && (attempt.environment === "sandbox" || attempt.inventoryReviewRequired === true);
  if (operation === "reviewReversal") return attempt.canReviewReversal === true && attempt.inventoryReviewRequired === true;
  if (operation === "simulateShipment") return attempt.canSimulateShipment && attempt.environment === "sandbox";
  return attempt.canCapture;
}

export function CreditKeyOrderPanel({ orderId }: { orderId: string }) {
  const identity = useAuthIdentity();
  if (!hasPermission(identity.userRole, "payments:view") || !identity.isAuthBridgeReady || !identity.isConvexAuthenticated) return null;
  return (
    <div className="print:hidden" data-print-hide="true">
      {identity.userRole === "admin" ? (
        <CreditKeyPanelBoundary key={`${orderId}-review`}>
          <CreditKeyOrderReview orderId={orderId} />
        </CreditKeyPanelBoundary>
      ) : null}
      <CreditKeyPanelBoundary key={`${orderId}-payments`}>
        <AuthorizedCreditKeyOrderPanel orderId={orderId} canManage={identity.userRole === "admin"} />
      </CreditKeyPanelBoundary>
      <CreditKeyPanelBoundary key={`${orderId}-storage`}>
        <CreditKeyStoragePanel orderId={orderId} canManage={identity.userRole === "admin"} />
      </CreditKeyPanelBoundary>
      {identity.userRole === "admin" ? <CreditKeyPanelBoundary key={`${orderId}-storage-bills`}>
        <StorageInvoices orderId={orderId} />
      </CreditKeyPanelBoundary> : null}
    </div>
  );
}

function StorageInvoices({ orderId }: { orderId: string }) {
  const billing = useQuery(api.creditKeyStorageBilling.getForOrder, { orderId }) as {
    enrolled: boolean; problem?: string; bills: Array<{ id: string; month: string; amount: number; total?: number; status: string; url?: string; error?: string }>;
  } | null | undefined;
  if (!billing || (!billing.enrolled && !billing.problem && !billing.bills.length)) return null;
  return <Card className="mb-4"><CardContent className="space-y-2 pt-4 text-sm">
    <h3 className="font-semibold">Storage invoices</h3>
    {billing.problem ? <p role="status" className="text-destructive">{billing.problem}</p> : null}
    {billing.bills.length ? <ul className="space-y-2">{billing.bills.map(bill => <li key={bill.id}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span>{bill.month} · <strong>{formatCurrency(bill.total ?? bill.amount)}</strong> · {bill.status.replaceAll("_", " ")}</span>
        {bill.url ? <a href={bill.url} target="_blank" rel="noopener noreferrer" className="font-medium underline underline-offset-4 hover:text-primary">View invoice</a> : null}
      </div>
      {bill.error ? <p role="status" className="text-xs text-destructive">{bill.error}</p> : null}
    </li>)}</ul> : <p className="text-muted-foreground">Automatic billing is set up. No storage invoices yet.</p>}
  </CardContent></Card>;
}

function AuthorizedCreditKeyOrderPanel({ orderId, canManage }: { orderId: string; canManage: boolean }) {
  const attempts = useQuery(api.creditKey.getOrderAttempts, { orderId }) as Attempt[] | undefined;
  const reconcile = useAction(api.creditKeyActions.adminReconcile);
  const cancel = useAction(api.creditKeyActions.cancel);
  const capture = useAction(api.creditKeyActions.capture);
  const refund = useAction(api.creditKeyActions.refund);
  const reviewReversal = useAction(api.creditKeyActions.completeReversalReview);
  const simulateShipment = useMutation(api.creditKey.simulateSandboxShipment);
  const [selected, setSelected] = React.useState<SelectedOperation | null>(null);
  const id = React.useId();

  if (attempts === undefined) return <p role="status" className="text-sm text-muted-foreground">Loading financing status…</p>;
  if (attempts.length === 0) return null;

  const confirmLabel = selected?.operation === "capture"
    ? "Capture financed payment"
    : selected?.operation === "cancel" ? "Cancel financing authorization"
      : selected?.operation === "refund" ? selected.attempt.inventoryReviewRequired ? "Refund financed purchase" : "Refund full sandbox payment"
        : selected?.operation === "reviewReversal" ? "Complete refund review"
        : selected?.operation === "simulateShipment" ? "Simulate sandbox shipment" : "Check provider status";

  async function confirmOperation(reason: string) {
    if (!selected) throw new Error("No payment operation selected");
    const latest = attempts?.find((attempt) => attempt._id === selected.attempt._id);
    // Recheck the live query at submit, in addition to the server-side lock and
    // authorization checks. Customer allowlist revocation is deliberately not
    // a dependency: existing orders must remain serviceable.
    if (!canManage || !latest || latest.amount !== selected.attempt.amount || !operationAllowed(latest, selected.operation)) {
      throw new Error("This payment action is no longer available");
    }
    try {
      const attemptId = selected.attempt._id;
      const inventoryReview = selected.attempt.inventoryReviewRequired ? { inventoryConfirmed: true } : {};
      if (selected.operation === "reconcile") await reconcile({ attemptId });
      else if (selected.operation === "cancel") await cancel({ attemptId, reason, ...inventoryReview });
      else if (selected.operation === "refund") await refund({ attemptId, reason, ...inventoryReview });
      else if (selected.operation === "reviewReversal") await reviewReversal({ attemptId, reason, inventoryConfirmed: true });
      else if (selected.operation === "simulateShipment") await simulateShipment({ attemptId, reason });
      else await capture({ attemptId });
      toast.success(selected.operation === "simulateShipment"
        ? "Sandbox shipment recorded for testing. No order or inventory was changed."
        : "Credit Key request processed. Review the updated financing status below.");
    } catch {
      toast.error("The Credit Key result could not be confirmed. Check provider status before another payment request.", { duration: Infinity });
      throw new Error("Credit Key operation not confirmed");
    }
  }

  return (
    <Card role="region" aria-labelledby={`${id}-title`} className="mb-4" data-print-hide="true">
      <CardHeader>
        <h2 id={`${id}-title`} className="font-semibold">Credit Key financing</h2>
        <CardDescription>
          Authorization, capture, shipment, and merchant payout are separate states. Capture does not verify settlement in Anthem's bank account. Customer repayments are handled by Credit Key.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <p className="text-sm text-muted-foreground">
          Existing payments remain serviceable when customer access is removed or new financing is paused. Paid warehouse storage is available for designated development tests; live storage funding is awaiting provider approval.
        </p>
        <ol className="space-y-6">
          {attempts.map((attempt) => {
            const knownStatus = Object.prototype.hasOwnProperty.call(STATUS_LABELS, attempt.status);
            return (
              <li key={attempt._id} className="space-y-3 rounded-lg border p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="space-y-1">
                    <p className="font-semibold">{formatCurrency(attempt.amount)}</p>
                    {attempt.environment === "sandbox" ? <p className="text-sm font-medium">Sandbox test — no live order funding</p> : null}
                    <p className="text-xs text-muted-foreground">Started {formatDate(attempt.createdAt, { month: "short", hour: "numeric", minute: "2-digit" })}</p>
                  </div>
                  <Badge variant="outline">{knownStatus ? STATUS_LABELS[attempt.status] : "Unrecognized payment state — review required"}</Badge>
                </div>
                <dl className="space-y-1 text-sm">
                  {attempt.providerStatus ? <div><dt className="inline text-muted-foreground">Provider state: </dt><dd className="inline">{attempt.providerStatus}</dd></div> : null}
                  <div><dt className="inline text-muted-foreground">Last update: </dt><dd className="inline">{formatDate(attempt.updatedAt, { month: "short", hour: "numeric", minute: "2-digit" })}</dd></div>
                </dl>
                {attempt.reviewReason ? <p role="status" className="break-words text-sm">{attempt.reviewReason}</p> : null}
                {attempt.disabledReason ? <p className="text-sm text-muted-foreground">{attempt.disabledReason}</p> : null}
                {canManage ? (
                  <div className="flex flex-wrap gap-3">
                    <Button type="button" variant="outline" disabled={!operationAllowed(attempt, "reconcile") || selected !== null} onClick={() => setSelected({ operation: "reconcile", attempt })}>
                      Check provider status
                    </Button>
                    {attempt.canCancel ? (
                      <Button type="button" variant="outline" disabled={!knownStatus || selected !== null} onClick={() => setSelected({ operation: "cancel", attempt })}>
                        Cancel financing authorization
                      </Button>
                    ) : null}
                    {attempt.canCapture ? (
                      <Button type="button" variant="outline" disabled={!knownStatus || selected !== null} onClick={() => setSelected({ operation: "capture", attempt })}>
                        Capture financed payment
                      </Button>
                    ) : null}
                    {attempt.canSimulateShipment && attempt.environment === "sandbox" ? (
                      <Button type="button" variant="outline" disabled={!operationAllowed(attempt, "simulateShipment") || selected !== null} onClick={() => setSelected({ operation: "simulateShipment", attempt })}>
                        Simulate sandbox shipment
                      </Button>
                    ) : null}
                    {operationAllowed(attempt, "refund") ? (
                      <Button type="button" variant="outline" disabled={!operationAllowed(attempt, "refund") || selected !== null} onClick={() => setSelected({ operation: "refund", attempt })}>
                        {attempt.inventoryReviewRequired ? "Refund financed purchase" : "Refund full sandbox payment"}
                      </Button>
                    ) : null}
                    {operationAllowed(attempt, "reviewReversal") ? (
                      <Button type="button" variant="outline" disabled={selected !== null} onClick={() => setSelected({ operation: "reviewReversal", attempt })}>
                        Complete refund review
                      </Button>
                    ) : null}
                  </div>
                ) : null}
                {attempt.status === "captured" && !operationAllowed(attempt, "refund") ? (
                  <p className="text-sm text-muted-foreground">This purchase needs payment or inventory review before a full refund. Refunds stay with Credit Key.</p>
                ) : null}
              </li>
            );
          })}
        </ol>
      </CardContent>
      {selected ? (
        <CreditKeyConfirmDialog
          title={`${confirmLabel}?`}
          confirmLabel={confirmLabel}
          destructive={selected.operation === "cancel" || selected.operation === "refund" || selected.operation === "reviewReversal"}
          requireReason={selected.operation === "cancel" || selected.operation === "refund" || selected.operation === "reviewReversal" || selected.operation === "simulateShipment"}
          acknowledgement={selected.attempt.inventoryReviewRequired && ["cancel", "refund", "reviewReversal"].includes(selected.operation)
            ? "Any shipped coffee has returned to Anthem, carrier shipments are cancelled, and all coffee can return to Anthem inventory." : undefined}
          description={selected.operation === "capture"
            ? selected.attempt.environment === "sandbox"
              ? `Capture ${formatCurrency(selected.attempt.amount)} through Credit Key in its sandbox? Verified capture records a test payment and applies the order’s selected fulfillment plan: paid customer storage, or preparation after a simulated shipment. Only synthetic test inventory can change. No live funds, carrier booking, customer emails, or loyalty points are involved.`
              : `Capture ${formatCurrency(selected.attempt.amount)} through Credit Key? This begins funding and customer repayment. The backend must confirm actual shipment; stored inventory is not eligible. This does not mark the order shipped.`
            : selected.operation === "cancel"
              ? selected.attempt.inventoryReviewRequired
                ? `Cancel the ${formatCurrency(selected.attempt.amount)} authorization and this order? Inventory and rewards will be restored only after Credit Key confirms cancellation.`
                : `Cancel the ${formatCurrency(selected.attempt.amount)} financing authorization? This does not cancel the Anthem order or release its inventory. Confirm the order's fulfillment plan separately.`
              : selected.operation === "refund"
                ? selected.attempt.inventoryReviewRequired
                  ? `Refund ${formatCurrency(selected.attempt.amount)} through Credit Key and cancel this purchase? Verified completion restores inventory and rewards and closes its unused stored-coffee contract.${selected.attempt.environment === "sandbox" ? " This is a sandbox test; no live money moves." : ""}`
                  : `Refund the full ${formatCurrency(selected.attempt.amount)} sandbox payment through Credit Key? This test has no funded Anthem order or inventory to reverse.`
                : selected.operation === "reviewReversal"
                  ? `Credit Key has reported a full reversal. Confirm inventory so Anthem can close this purchase and reconcile its payment and rewards. This checks provider status without requesting another refund.`
                : selected.operation === "simulateShipment"
                  ? `Record a simulated shipment for this ${formatCurrency(selected.attempt.amount)} sandbox financing attempt? This enables sandbox capture testing only. It does not ship goods, change the Anthem order or delivery, move inventory, or create live payments or loyalty points.`
                : "Read the provider's current order state and reconcile this payment. Verified provider changes may update the Anthem payment status. This does not retry authorization, capture, or refund."}
          failureMessage="The outcome could not be confirmed, or this action is no longer available. Close this dialog and check provider status before making another payment request. Do not retry a money-moving request without a confirmed state."
          onConfirm={confirmOperation}
          onClose={() => setSelected(null)}
        />
      ) : null}
    </Card>
  );
}
